import React, { useEffect, useRef, useState, useMemo } from 'react';
import LabelEventGraph from '../LabelEventGraph';
import * as glossaire_type_de_graphe from '../../../../../ressources/glossaires/glossaire_type_de_graphe';
import { fetchGraphData } from './labelEventGraphApi';

const GRAPH_ID = 1;
const HEADER_HEIGHT = 20;
const PADDING = 8;

// Labels IA — iconType = clé exacte dans IA_CATEGORIES → emoji + couleur catégorie
const IA_DEMO_LABELS = [
  {
    iconType: 'Marteau-piqueur',
    label: 'Marteau-piqueur',
    note: 'Rue Gambetta — percement trottoir',
    labelType: 'IA',
  },
  {
    iconType: 'Sciage',
    label: 'Sciage',
    note: 'Découpe bordure béton',
    labelType: 'IA',
  },
  {
    iconType: 'Moteur lourd',
    label: 'Moteur lourd',
    note: 'Compacteur Dynapac CA2500',
    labelType: 'IA',
  },
  {
    iconType: 'Démarrage du moteur',
    label: 'Démarrage du moteur',
    note: 'Mise en route grue',
    labelType: 'IA',
  },
  {
    iconType: 'Camion',
    label: 'Camion',
    note: 'Benne à gravats',
    labelType: 'IA',
  },
  {
    iconType: 'Bip de recul',
    label: 'Bip de recul',
    note: 'Pelleteuse en manœuvre',
    labelType: 'IA',
  },
  {
    iconType: 'Détonation',
    label: 'Détonation',
    note: 'Carrière — tir de mine',
    labelType: 'IA',
  },
  {
    iconType: 'Fracas',
    label: 'Fracas',
    note: 'Chute de matériaux',
    labelType: 'IA',
  },
  {
    iconType: 'Grondement',
    label: 'Grondement',
    note: 'Tonnerre au loin',
    labelType: 'IA',
  },
  {
    iconType: 'Animal',
    label: 'Animal',
    note: 'Faune locale — matin',
    labelType: 'IA',
  },
  {
    iconType: 'Urbain',
    label: 'Urbain',
    note: 'Manifestation avenue principale',
    labelType: 'IA',
  },
  {
    iconType: 'Voix humaine',
    label: 'Voix humaine',
    note: 'Équipe de chantier',
    labelType: 'IA',
  },
  {
    iconType: 'circle',
    label: 'Son non classifié',
    note: 'Détection sans catégorie',
    labelType: 'IA',
  },
];

// Labels MANUAL — pour tester la détection d'emoji via detectCategory(label)
const MANUAL_DEMO_LABELS = [
  {
    iconType: 'diamond',
    label: 'Marteau',
    note: 'Jalon → emoji détecté automatiquement',
    labelType: 'JALON',
  },
  {
    iconType: 'diamond',
    label: 'Camion',
    note: 'Jalon → emoji détecté automatiquement',
    labelType: 'JALON',
  },
  {
    iconType: 'diamond',
    label: 'Visite terrain',
    note: 'Jalon → texte inconnu → cercle bleu',
    labelType: 'JALON',
  },
  {
    iconType: 'diamond',
    label: 'Pic anormal',
    note: 'Valeur hors-seuil constatée',
    labelType: 'JALON',
  },
];

const buildDemoLabels = (tArr) => {
  if (!tArr || tArr.length < 2) return [];
  const t0 = Number(tArr[0]);
  const tN = Number(tArr[tArr.length - 1]);
  const all = [...IA_DEMO_LABELS, ...MANUAL_DEMO_LABELS];
  const n = all.length;
  return all.map((def, i) => ({
    id: -(i + 1),
    t: Math.round(t0 + (tN - t0) * ((i + 1) / (n + 1))),
    pk: null,
    idSensor: null,
    positionInSensor: null,
    ...def,
  }));
};

const LabelEventGraphGeoDemo = () => {
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ width: undefined, height: undefined });
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [signalData, setSignalData] = useState([]);
  const [configBase, setConfigBase] = useState({});
  const [donneesGraphe, setDonneesGraphe] = useState(null);
  const [markerSize, setMarkerSize] = useState(10);

  useEffect(() => {
    fetchGraphData(GRAPH_ID)
      .then((res) => {
        if (!res.ok) {
          setFetchError(res.error);
          setLoading(false);
          return;
        }
        const { config, data, graphCurves, id_sensor } = res;
        setSignalData(data);
        setConfigBase({
          title: config.title || '',
          subtitle: config.subtitle || '',
          xaxis: config.xaxis || {},
          yaxis: config.yaxis || {},
          yaxis2: config.yaxis2 || {},
        });
        setDonneesGraphe({
          config: {
            graph_id: GRAPH_ID,
            graph_type_id: glossaire_type_de_graphe.graphe_x_t_label,
            title: config.title || '',
            sensor_name: config.sensor_name || '',
            prefix: config.prefix || '',
          },
          graphCurves: graphCurves || [],
          id_sensor: id_sensor || [],
          data,
        });
        setLoading(false);
      })
      .catch(() => {
        setFetchError('Impossible de charger les données du graphe');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        setDims({
          width: containerRef.current.offsetWidth - 28,
          height: window.innerHeight - HEADER_HEIGHT - PADDING,
        });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const demoLabels = useMemo(() => {
    const tArr = signalData[0]?.x || signalData[0]?.t || [];
    return buildDemoLabels(tArr);
  }, [signalData]);

  const configuration = useMemo(
    () => ({
      ...configBase,
      markerSize,
      labels: demoLabels,
    }),
    [configBase, markerSize, demoLabels]
  );

  if (loading)
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: 'Montserrat, sans-serif',
        }}
      >
        Chargement…
      </div>
    );

  if (fetchError)
    return (
      <div
        style={{
          color: 'red',
          padding: 24,
          fontFamily: 'Montserrat, sans-serif',
        }}
      >
        Erreur : {fetchError}
      </div>
    );

  return (
    <div
      ref={containerRef}
      style={{ height: '100vh', overflow: 'hidden', position: 'relative' }}
    >
      <div
        style={{
          position: 'absolute',
          top: 8,
          left: 8,
          zIndex: 4000,
          background: 'rgba(255,255,255,0.93)',
          border: '1px solid #ddd',
          borderRadius: 8,
          padding: '6px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontFamily: 'Montserrat, sans-serif',
          fontSize: 12,
          pointerEvents: 'auto',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <span style={{ color: '#444', fontWeight: 500 }}>Taille icônes</span>
        <input
          type="range"
          min={8}
          max={40}
          step={2}
          value={markerSize}
          onChange={(e) => setMarkerSize(Number(e.target.value))}
          style={{ width: 110 }}
        />
        <span
          style={{
            color: '#0077b6',
            fontWeight: 600,
            minWidth: 22,
            textAlign: 'right',
          }}
        >
          {markerSize}
        </span>
      </div>

      <LabelEventGraph
        donnees={signalData}
        configuration={configuration}
        donneesGraphe={donneesGraphe}
        dimensionWidth={dims.width}
        dimensionHeight={dims.height}
        switchAfficherModifierIntervalle={() => {}}
        viewobj_json_content={{ displayModeBar: true }}
      />
    </div>
  );
};

export default LabelEventGraphGeoDemo;
